import { useEffect, useState } from "react";
import { listWebhookAttempts, replayWebhook, type WebhookAttempt } from "@/lib/api";
import { formatDate, formatRelative } from "@/lib/format";
import { RotateCcw } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { DataTable, Badge, statusColor, type ColumnDef } from "@/components/DataTable";

export function WebhooksPage() {
  const [attempts, setAttempts] = useState<WebhookAttempt[]>([]);
  const [loading, setLoading] = useState(true);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [filterStatus, setFilterStatus] = useState("");
  const [replaying, setReplaying] = useState<string | null>(null);

  const load = async (reset = false) => {
    setLoading(true);
    try {
      const params: any = { limit: 50 };
      if (!reset && cursor) params.cursor = cursor;
      if (filterStatus) params.status = filterStatus;
      const r = await listWebhookAttempts(params);
      setAttempts(reset ? r.data : [...attempts, ...r.data]);
      setCursor(r.next_cursor ?? null);
      setHasMore(r.has_more);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setCursor(null);
    load(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus]);

  const handleReplay = async (id: string) => {
    if (!confirm("Renvoyer ce webhook ?")) return;
    setReplaying(id);
    try {
      await replayWebhook(id);
      await load(true);
    } catch (e: any) {
      alert(`Échec du replay: ${e.message ?? "erreur"}`);
    } finally {
      setReplaying(null);
    }
  };

  const columns: ColumnDef<WebhookAttempt>[] = [
    {
      key: "date",
      header: "Date",
      minWidth: 100,
      cell: (a) => <span className="text-xs text-[#7e8299]" title={formatDate(a.created_at)}>{formatRelative(a.created_at)}</span>,
    },
    {
      key: "event",
      header: "Event",
      minWidth: 160,
      cell: (a) => <span className="text-sm font-medium">{a.event_type}</span>,
    },
    {
      key: "url",
      header: "URL",
      minWidth: 280,
      cell: (a) => (
        <span className="font-mono text-xs text-[#7e8299] truncate block max-w-[340px]" title={a.url}>
          {a.url}
        </span>
      ),
    },
    {
      key: "status",
      header: "Statut",
      minWidth: 110,
      cell: (a) => <Badge color={statusColor(a.status)}>{a.status}</Badge>,
    },
    {
      key: "http",
      header: "HTTP",
      align: "right",
      minWidth: 80,
      cell: (a) =>
        a.last_status_code ? (
          <Badge color={a.last_status_code >= 200 && a.last_status_code < 300 ? "success" : "danger"}>
            {a.last_status_code}
          </Badge>
        ) : (
          <span className="text-[#7e8299]">—</span>
        ),
    },
    {
      key: "attempts",
      header: "Tentatives",
      align: "right",
      minWidth: 100,
      cell: (a) => <span className="text-sm font-semibold">{a.attempt_count}</span>,
    },
    {
      key: "error",
      header: "Erreur",
      minWidth: 220,
      cell: (a) => (
        <span className="text-xs text-danger-700 truncate block max-w-[260px]" title={a.last_error ?? ""}>
          {a.last_error ?? "—"}
        </span>
      ),
    },
    {
      key: "actions",
      header: "",
      align: "right",
      minWidth: 100,
      cell: (a) =>
        a.status === "failed" || a.status === "abandoned" ? (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleReplay(a.id);
            }}
            disabled={replaying === a.id}
            className="btn btn-light-primary btn-sm"
          >
            <RotateCcw size={12} />
            {replaying === a.id ? "..." : "Replay"}
          </button>
        ) : null,
    },
  ];

  const filters = (
    <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="form-input py-2 px-3 text-sm w-auto">
      <option value="">Tous statuts</option>
      <option value="pending">Pending</option>
      <option value="retrying">Retrying</option>
      <option value="succeeded">Succeeded</option>
      <option value="failed">Failed</option>
      <option value="abandoned">Abandoned</option>
    </select>
  );

  return (
    <div>
      <PageHeader
        title="Webhooks sortants"
        breadcrumbs={[{ label: "Informatique" }, { label: "Webhooks" }]}
      />

      <DataTable<WebhookAttempt>
        title="Notifications marchand"
        subtitle="Tentatives signées HMAC, avec replay manuel possible"
        columns={columns}
        data={attempts}
        rowKey={(a) => a.id}
        loading={loading && attempts.length === 0}
        filtersSlot={filters}
        loadMore={hasMore ? { hasMore, onLoadMore: () => load(false), loading } : undefined}
      />
    </div>
  );
}
